import { useEffect, useState, useCallback, useMemo } from "react";
import { useDeviceActivation } from "@/hooks/useDeviceActivation";
import { useMedias } from "@/hooks/useMedias";
import { useRealtimeMediasSync } from "./useRealtimeMediasSync";
import { usePlayerState } from "./usePlayerState";
import { usePlayerMediaFiltering } from "./usePlayerMediaFiltering";
import { useVenueAds } from "./useVenueAds"; // Importar hook de venue ads
import type { Media } from '@/types';



interface UsePlayerReturn {
    isSettingsOpen: boolean;
    setIsSettingsOpen: (open: boolean) => void;
    currentMediaIndex: number;
    setCurrentMediaIndex: (index: number) => void;
    brightness: number;
    saveBrightness: (value: number) => void;
    deviceId: string;
    deviceRecordId: string | null; // UUID do dispositivo na tabela devices (para comandos remotos)
    showWeatherClock: boolean;
    setShowWeatherClock: (show: boolean) => void;
    showNewsTicker: boolean;
    setShowNewsTicker: (show: boolean) => void;
    selectedScreen: string;
    deviceCode: string;
    isActivated: boolean;
    activatedAccount: string | null;
    activatedUserId: string | null; // Adicionado para shutdown system
    availableScreens: Array<{ id: string }>;
    filteredMedias: Media[];
    checkActivation: () => void;
    isSyncing?: boolean;
    hasActiveSubscription: boolean;
    refetchMedias?: () => Promise<void>;
}

export const usePlayer = (deviceCodeParam?: string): UsePlayerReturn => {
    const {
        isSettingsOpen,
        setIsSettingsOpen,
        currentMediaIndex,
        setCurrentMediaIndex,
        brightness,
        saveBrightness,
        deviceId,
        showWeatherClock,
        setShowWeatherClock,
        showNewsTicker,
        setShowNewsTicker,
        organizationId
    } = usePlayerState();

    console.log('🔍 [PLAYER] deviceCodeParam recebido:', deviceCodeParam);

    const {
        deviceCode,
        isActivated,
        activatedAccount,
        activatedUserId,
        deviceRecordId,
        availableScreens,
        deviceSettings,
        checkActivation,
        associatedScreenId,
        hasActiveSubscription,
    } = useDeviceActivation(deviceCodeParam);

    console.log('🔍 [PLAYER] deviceCode do useDeviceActivation:', deviceCode);
    console.log('🔍 [PLAYER] isActivated:', isActivated);
    console.log('🔍 [PLAYER] associatedScreenId:', associatedScreenId);
    const [isSyncing, setIsSyncing] = useState(false);



    // Carrega as mídias usando o ID do usuário ativado
    const selectedScreen = associatedScreenId || (Array.isArray(availableScreens) && availableScreens.length > 0 ? String(availableScreens[0].id || availableScreens[0]) : "");

    // Verificar se foi ativado via Companion (motorista/parceiro)
    // No modo Companion, não precisa de activatedUserId pois busca TODAS as mídias
    // Verificar se foi ativado via Companion (motorista/parceiro)
    // No modo Companion, não precisa de activatedUserId pois busca TODAS as mídias
    const isCompanionMode = typeof window !== 'undefined' && sessionStorage.getItem('player_activated_by_companion') === 'true';

    // Modo Legacy (Dashboard): precisa de activatedUserId
    // Modo Companion: não precisa de activatedUserId (busca todas as mídias)
    const shouldLoadMedias = isActivated && hasActiveSubscription && (activatedUserId || isCompanionMode);

    const mediasResult = useMedias(shouldLoadMedias ? (activatedUserId || '') : '', { ignoreStorageFallback: true });
    const allMedias = mediasResult?.medias || [];

    // Otimizar a função refetchMedias com useCallback para evitar recriação
    const refetchMedias = useCallback(async () => {
        if (!mediasResult?.refetch) return;

        setIsSyncing(true);
        try {
            await mediasResult.refetch();
        } catch (error) {
            console.error('❌ Erro ao atualizar mídias:', error);
        } finally {
            setIsSyncing(false);
        }
    }, [mediasResult?.refetch]);

    // Sincronização em tempo real otimizada - APENAS este hook de sincronização
    useRealtimeMediasSync({
        deviceCode,
        isActivated,
        activatedAccount,
        checkActivation,
        activatedUserId,
        organizationId,
        associatedScreenId
    });

    // Importante: checkActivation precisa estar nas dependências dos callbacks

    // Filtra mídias com agendamento
    const {
        filteredMedias = [],
        filteredMediasHash = '',
        filteredMediaIds = []
    } = usePlayerMediaFiltering({
        medias: allMedias,
        selectedScreen,
        userId: activatedUserId || ''
    }) || {};

    // ---------------------------------------------------------------------------
    // Lógica de Ciclo de anúncios (Venue Ads)
    // ---------------------------------------------------------------------------
    const { venueAds, cycleConfig } = useVenueAds(activatedUserId);

    const mergedMedias = useMemo(() => {
        // Se não houver mídias internas, retorne apenas o que tiver (ou vazio)
        if (!filteredMedias || filteredMedias.length === 0) {
            // Se não tem interna mas tem ads, mostra ads? Geralmente o player precisa de interna.
            // Assumindo comportamento padrão: sem interna, player pode ficar vazio ou mostrar placeholder.
            // Mas se tiver ads, poderíamos mostrar. Por enquanto, retornamos vazio para segurança ou apenas ads.
            return venueAds.length > 0 ? venueAds : [];
        }

        // Se não houver ads externos ou configuração inválida, retorna apenas internas
        if (!venueAds || venueAds.length === 0 || cycleConfig.internal <= 0) {
            return filteredMedias;
        }

        // Algoritmo de interleaving
        const internalStep = cycleConfig.internal;
        const externalStep = cycleConfig.external;

        // 1. Expandir a lista interna para garantir que o ciclo feche perfeitamente
        // O comprimento deve ser múltiplo de internalStep para que a proporção se mantenha no loop
        const desiredInternalLength = Math.ceil(filteredMedias.length / internalStep) * internalStep;
        // Se a lista original for menor que o ciclo, precisamos expandir pelo menos até o ciclo
        const targetLength = Math.max(desiredInternalLength, internalStep);

        const expandedInternals: Media[] = [];
        for (let i = 0; i < targetLength; i++) {
            expandedInternals.push(filteredMedias[i % filteredMedias.length]);
        }

        // 2. Intercalar com ads externos
        const finalPlaylist: Media[] = [];
        let adPointer = 0;

        for (let i = 0; i < expandedInternals.length; i++) {
            finalPlaylist.push(expandedInternals[i]);

            // Se completou um bloco de internas, insere o bloco de externas
            if ((i + 1) % internalStep === 0) {
                for (let j = 0; j < externalStep; j++) {
                    const ad = venueAds[adPointer % venueAds.length];
                    // Marcar como ad externo para diferenciação se necessário (opcional)
                    finalPlaylist.push({ ...ad, is_external: true } as Media & { is_external?: boolean });
                    adPointer++;
                }
            }
        }

        return finalPlaylist;
    }, [filteredMedias, venueAds, cycleConfig]);

    // Listener para atualização global de todos os players - otimizado com useCallback
    const handleGlobalRefresh = useCallback(async (event: Event) => {
        const customEvent = event as CustomEvent;
        console.log(`🔄 [GLOBAL REFRESH] Evento recebido no player`, { deviceId, detail: customEvent.detail });

        if (isActivated) {
            console.log(`🔄 [GLOBAL REFRESH] Player ${deviceId} ativo - iniciando atualização...`);
            await refetchMedias();
            checkActivation();
        }
    }, [isActivated, deviceId, refetchMedias, checkActivation]);

    useEffect(() => {
        window.addEventListener('dashboardRefreshAllPlayers', handleGlobalRefresh);
        return () => {
            window.removeEventListener('dashboardRefreshAllPlayers', handleGlobalRefresh);
        };
    }, [handleGlobalRefresh]);

    // Listener otimizado para atualizações específicas - com useCallback
    const handleDashboardUpdate = useCallback(async (event: CustomEvent<{ type: string, data: Record<string, unknown> }>) => {
        const { type, data } = event.detail;

        if (!isActivated || !selectedScreen || isSyncing) return;

        try {
            if (type === 'media_updated' && data) {
                const updatedScreens = Array.isArray(data.selected_screens)
                    ? data.selected_screens.map(String)
                    : [];

                const affectsThisScreen = updatedScreens.includes(selectedScreen);

                if (affectsThisScreen) {
                    await refetchMedias();
                }
            } else if (type === 'media_display_time_updated') {
                await refetchMedias();
            }
        } catch (error) {
            console.error('❌ Erro ao processar atualização:', error);
        }
    }, [isActivated, selectedScreen, isSyncing, refetchMedias]);

    useEffect(() => {
        window.addEventListener('dashboardDataUpdate', handleDashboardUpdate as EventListener);
        return () => {
            window.removeEventListener('dashboardDataUpdate', handleDashboardUpdate as EventListener);
        };
    }, [handleDashboardUpdate]);

    // Retorno memoizado para evitar recriações desnecessárias
    return useMemo(() => ({
        isSettingsOpen,
        setIsSettingsOpen,
        currentMediaIndex,
        setCurrentMediaIndex,
        brightness,
        saveBrightness,
        deviceId,
        deviceRecordId, // UUID do dispositivo na tabela devices (para comandos remotos)
        showWeatherClock,
        setShowWeatherClock,
        showNewsTicker,
        setShowNewsTicker,
        selectedScreen,
        deviceCode,
        isActivated,
        activatedAccount,
        activatedUserId: activatedUserId || null, // Adicionado para shutdown system
        availableScreens,
        filteredMedias: mergedMedias, // Retorna a lista mesclada
        internalMediasOnly: filteredMedias, // Expõe apenas internas se necessário
        checkActivation,
        isSyncing,
        hasActiveSubscription,
        refetchMedias // Adicionar refetchMedias para refresh inteligente
    }), [
        isSettingsOpen,
        currentMediaIndex,
        brightness,
        deviceId,
        deviceRecordId,
        showWeatherClock,
        showNewsTicker,
        selectedScreen,
        deviceCode,
        isActivated,
        activatedAccount,
        availableScreens,
        filteredMedias,
        isSyncing,
        checkActivation,
        hasActiveSubscription,
        refetchMedias,
        mergedMedias // Adicionar na dependência
    ]);
};
